import { useMemo, useEffect, useState, useContext } from 'react';
import { formatNumWithKMB } from 'utils/helperMethods';
import Button from 'components/atoms/Button';
import { CustomModal } from 'CustomComponentCss';
import DownSvg from 'assets/svg/Down';
import { Spin, Cascader } from 'antd';
import { useFetchSummaryData } from 'hooks/promoRecommendation/useFetchSummaryData';
import useOptimismPlan from 'hooks/promoRecommendation/useOptimismPromo';
import useFetchEventDataList from 'hooks/promoRecommendation/useFetchImportEventData';
import useOptimizationStatus from 'hooks/promoRecommendation/useOptimizationStatus';
import { antIcon } from 'components/molecules/Filter/FilterApplyButton';
import MultiSelectDropdown from 'components/molecules/MultiSelectDropdown';

type props = {
    selectedPlan?: any;
    openOptimizeDialogBox: any;
    setOpenOptimizeDialogBox: any;
    onOptimizeComplete: Function;
    eventsListData: any;
    fetchOptimizedPlan: Function;
};

const ConstraintRow = ({
    label,
    originalValue,
    inputType,
    minValue,
    maxValue,
    onMinChange,
    onMaxChange,
    dropdownValue,
    onDropdownChange,
    dropdownOptions,
    isMultiSelect = false,
}: {
    label: string;
    originalValue: string;
    inputType: 'range' | 'single' | 'dropdown';
    minValue?: string;
    maxValue?: string;
    onMinChange?: (value: string) => void;
    onMaxChange?: (value: string) => void;
    dropdownValue?: any;
    onDropdownChange?: (value: any) => void;
    dropdownOptions?: any[];
    isMultiSelect?: boolean;
}) => {
    return (
        <div className="flex w-full justify-between flex-col">
            <div
                style={{ height: '43px' }}
                className="flex justify-between w-full items-center border-solid border-0 border-t border-borderGrey p-2 !text-fontGray"
            >
                <div className="flex w-full">
                    <div className="flex py-2 w-full">
                        <div className="w-1/2 ml-2 font-xs !w-full">
                            <span>{label}</span>
                        </div>
                        <div className="w-1/3 ml-2 flex justify-end mr-20">
                            {originalValue ? formatNumWithKMB(originalValue as any) : ''}
                        </div>
                        <div className="w-1/2 flex justify-center">
                            {inputType === 'range' ? (
                                <span className="flex justify-center justify-between gap-2" style={{ width: '220px' }}>
                                    <span className="border border-purpleFont rounded">
                                        <input
                                            className="m-0.5 focus:outline-none pl-1"
                                            style={{ width: '90px', height: '24px' }}
                                            value={minValue || ''}
                                            placeholder="min"
                                            type="number"
                                            onChange={(e: any) => onMinChange?.(e.target.value)}
                                        />
                                    </span>
                                    -
                                    <span className="border border-purpleFont rounded">
                                        <input
                                            className="m-0.5 focus:outline-none pl-1"
                                            style={{ width: '90px', height: '24px' }}
                                            value={maxValue || ''}
                                            placeholder="max"
                                            type="number"
                                            onChange={(e: any) => onMaxChange?.(e.target.value)}
                                        />
                                    </span>
                                </span>
                            ) : inputType === 'dropdown' && isMultiSelect ? (
                                <MultiSelectDropdown
                                    style={{ width: '220px' }}
                                    className="dropdown-height dropdown-width-alt"
                                    selectedDropdownItems={dropdownValue || []}
                                    dropdownList={dropdownOptions || []}
                                    onPopupClose={(tempSelectedDimensionList) => {
                                        onDropdownChange?.(tempSelectedDimensionList);
                                    }}
                                    onChange={(val: any, item: any, setTempSelectedDimensionList) => {
                                        // Update the temporary state for immediate UI feedback
                                        setTempSelectedDimensionList(item);
                                    }}
                                />
                            ) : null}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

const OptimizeScenarioModel = ({
    selectedPlan,
    openOptimizeDialogBox,
    setOpenOptimizeDialogBox,
    onOptimizeComplete,
    eventsListData,
    fetchOptimizedPlan,
}: props) => {
    const cascaderOptions = [
        {
            value: 'maximize',
            label: 'Maximize',
            children: [
                {
                    value: 'maximize_stat_volume',
                    label: 'Total Volume',
                },
                {
                    value: 'incremental_stat_volume',
                    label: 'Incremental Volume',
                },
                {
                    value: 'incremental_profit',
                    label: 'Incremental Profit',
                },
                {
                    value: 'incremental_retailer_profit',
                    label: 'Incremental Retailer Profit',
                },
                {
                    label: 'Incremental NSV',
                    value: 'incremental_nsv',
                },
                {
                    label: 'Total Gross Profit',
                    value: 'total_gross_profit',
                },
                // {
                //     value: 'retail_sales_value',
                //     label: 'Retail Sales Value',
                // },
                {
                    value: 'total_net_sales',
                    label: 'Total Net Sales Value',
                },
                {
                    value: 'total_retailer_profit',
                    label: 'Total Retailer Profit',
                },
            ],
        },
        {
            value: 'minimize',
            label: 'Minimize',
            children: [
                {
                    value: 'total_spends',
                    label: 'Total Promo Spends',
                },
            ],
        },
    ];
    const [isOptimizing, setIsOptimizing] = useState(false);

    const [maximizeOption, setMaximizeOption] = useState<any>();

    const [promoTradeMax, setPromoTradeMax] = useState<any>();
    const [promoTradeMin, setPromoTradeMin] = useState<any>();
    const [tradeRateSpendMin, setTradeRateSpendMin] = useState<any>();
    const [tradeRateSpendMax, setTradeRateSpendMax] = useState<any>();
    const [selectedCampaignTypes, setSelectedCampaignTypes] = useState<any[]>([]);
    const [retailMarginMin, setRetailMarginMin] = useState<any>();
    const [retailMarginMax, setRetailMarginMax] = useState<any>();
    const [minTotalRoi, setMinTotalRoi] = useState<any>();
    const [minEventRoi, setMinEventRoi] = useState<any>();

    // New state variables for range inputs
    const [totalVolumeMin, setTotalVolumeMin] = useState<any>();
    const [totalVolumeMax, setTotalVolumeMax] = useState<any>();
    const [totalNetSalesMin, setTotalNetSalesMin] = useState<any>();
    const [totalNetSalesMax, setTotalNetSalesMax] = useState<any>();
    const [totalRetailSalesMin, setTotalRetailSalesMin] = useState<any>();
    const [totalRetailSalesMax, setTotalRetailSalesMax] = useState<any>();
    const [totalGrossProfitMin, setTotalGrossProfitMin] = useState<any>();
    const [totalGrossProfitMax, setTotalGrossProfitMax] = useState<any>();
    const [incrementalVolumeMin, setIncrementalVolumeMin] = useState<any>();
    const [incrementalVolumeMax, setIncrementalVolumeMax] = useState<any>();
    const [incrementalProfitMin, setIncrementalProfitMin] = useState<any>();
    const [retailerIncrementalProfitMin, setRetailerIncrementalProfitMin] = useState<any>();
    const [retailerIncrementalProfitMax, setRetailerIncrementalProfitMax] = useState<any>();
    const [incrementalProfitMax, setIncrementalProfitMax] = useState<any>();
    const [retailerProfitMin, setRetailerProfitMin] = useState<any>();
    const [retailerProfitMax, setRetailerProfitMax] = useState<any>();
    const [incrementalNsvMin, setIncrementalNsvMin] = useState<any>();
    const [incrementalNsvMax, setIncrementalNsvMax] = useState<any>();
    const {
        data: summaryData,
        loading: optimizeOriginalLoading,
        error: summaryError,
        fetchSummaryData,
    } = useFetchSummaryData();

    const {
        optimizePlan,
        loading: optimizePlanLoading,
        error: optimizePlanError,
        setError: setOptimizePlanError,
    } = useOptimismPlan({ onComplete: onOptimizePlanComplete });

    // Fetch event data for campaign type dropdown
    const {
        data: eventData,
        loading: eventDataLoading,
        error: eventDataError,
        fetchEventDataList,
    } = useFetchEventDataList();

    // Monitor optimization status
    const { optimizationData } = useOptimizationStatus(
        selectedPlan?.planId,
        true,
        selectedPlan?.planName,
        onOptimizePlanCompleteWithAction,
        setIsOptimizing,
        isOptimizing,
    );

    //called when optimize plan is completed
    function onOptimizePlanComplete() {
        onOptimizeComplete();
        setOpenOptimizeDialogBox();
    }

    const handleCancel = () => {
        setOpenOptimizeDialogBox(false);
        setMaximizeOption(null);
    };

    const optimizeOriginalData: any = useMemo(() => {
        if (summaryData?.length > 0) {
            const data: any = {};

            const roiVal = summaryData?.find((i: any) => i.KPI === 'Promo ROI')?.[selectedPlan.planName]?.val;
            data.roi = roiVal ? roiVal : '0';

            const totalTradeSpendVal = summaryData?.find((i: any) => i.KPI === 'Promo Spends')?.[selectedPlan.planName]
                ?.val;
            data.totalTradeSpend = totalTradeSpendVal ? totalTradeSpendVal : '0';

            // Add new fields for range inputs
            const totalVolumeVal = summaryData?.find((i: any) => i.KPI === 'Total Volume')?.[selectedPlan.planName]
                ?.val;
            data.totalVolume = totalVolumeVal ? totalVolumeVal : '0';

            const totalNetSalesVal = summaryData?.find((i: any) => i.KPI === 'Total NSV')?.[selectedPlan.planName]?.val;
            data.totalNetSales = totalNetSalesVal ? totalNetSalesVal : '0';

            const totalRetailSalesVal = summaryData?.find((i: any) => i.KPI === 'Total Retail Sales Value')?.[
                selectedPlan.planName
            ]?.val;
            data.totalRetailSales = totalRetailSalesVal ? totalRetailSalesVal : '0';

            const totalGrossProfitVal = summaryData?.find((i: any) => i.KPI === 'Total Nomad Foods Profit')?.[
                selectedPlan.planName
            ]?.val;
            data.totalGrossProfit = totalGrossProfitVal ? totalGrossProfitVal : '0';

            const incrementalVolumeVal = summaryData?.find((i: any) => i.KPI === 'Incremental Volume')?.[
                selectedPlan.planName
            ]?.val;
            data.incrementalVolume = incrementalVolumeVal ? incrementalVolumeVal : '0';

            const incrementalProfitVal = summaryData?.find((i: any) => i.KPI === 'Incremental Profit')?.[
                selectedPlan.planName
            ]?.val;
            data.incrementalProfit = incrementalProfitVal ? incrementalProfitVal : '0';

            const totalRetailerProfitVal = summaryData?.find((i: any) => i.KPI === 'Total Retailer Profit')?.[
                selectedPlan.planName
            ]?.val;
            data.totalRetailerProfit = totalRetailerProfitVal ? totalRetailerProfitVal : '0';

            const incrementalNsvVal = summaryData?.find((i: any) => i.KPI === 'Incremental NSV')?.[
                selectedPlan.planName
            ]?.val;
            data.incrementalNsv = incrementalNsvVal ? incrementalNsvVal : '0';

            const retailerIncrementalProfitVal = summaryData?.find(
                (i: any) => i.KPI === 'Retailer Incremental Profit',
            )?.[selectedPlan.planName]?.val;
            data.retailerIncrementalProfit = retailerIncrementalProfitVal ? retailerIncrementalProfitVal : '0';
            
            return data;
        }
        return {};
    }, [summaryData]);

    const eventOptions = useMemo(() => {
        if (eventsListData && Array.isArray(eventsListData?.eventsData) && eventsListData?.eventsData?.length > 0) {
            return eventsListData?.eventsData?.map((event: any) => {
                const { eventId, eventName } = event?.edit;
                return {
                    label: eventName,
                    value: eventName,
                    eventName: eventName,
                    eventId: eventId,
                };
            });
        }

        return [];
    }, [eventsListData?.eventsData]);

    //only call api once during the load of screen and if retailer present
    useEffect(() => {
        if (openOptimizeDialogBox) {
            fetchSummaryData({
                variables: {
                    planIds: [selectedPlan?.planId],
                    timePeriod: 'full_year',
                },
            });

            // Fetch event data for campaign type dropdown
            if (selectedPlan?.planId) {
                fetchEventDataList({
                    planId: selectedPlan.planId,
                });
            }

            setMaximizeOption(null);
            setPromoTradeMin('');
            setPromoTradeMax('');
            setTradeRateSpendMin('');
            setTradeRateSpendMax('');
            setRetailMarginMin('');
            setRetailMarginMax('');
            setMinTotalRoi('');
            setMinEventRoi('');
            setSelectedCampaignTypes([]);
            // Reset all new range input state variables
            setTotalVolumeMin('');
            setTotalVolumeMax('');
            setTotalNetSalesMin('');
            setTotalNetSalesMax('');
            setTotalRetailSalesMin('');
            setTotalRetailSalesMax('');
            setTotalGrossProfitMin('');
            setTotalGrossProfitMax('');
            setIncrementalVolumeMin('');
            setIncrementalVolumeMax('');
            setIncrementalProfitMin('');
            setIncrementalProfitMax('');
            setRetailerIncrementalProfitMin('');
            setRetailerIncrementalProfitMax('');
            setRetailerProfitMin('');
            setRetailerProfitMax('');
            setIncrementalNsvMin('');
            setIncrementalNsvMax('');
        }
    }, [openOptimizeDialogBox]);

    // Determine if at least one constraint has been selected/provided
    const isAnyConstraintSelected = useMemo(() => {
        const hasAtLeastOneValue = (min: any, max: any) => (min && min !== '') || (max && max !== '');

        return (
            hasAtLeastOneValue(promoTradeMin, promoTradeMax) ||
            hasAtLeastOneValue(tradeRateSpendMin, tradeRateSpendMax) ||
            hasAtLeastOneValue(totalVolumeMin, totalVolumeMax) ||
            hasAtLeastOneValue(totalNetSalesMin, totalNetSalesMax) ||
            hasAtLeastOneValue(totalRetailSalesMin, totalRetailSalesMax) ||
            hasAtLeastOneValue(totalGrossProfitMin, totalGrossProfitMax) ||
            hasAtLeastOneValue(incrementalVolumeMin, incrementalVolumeMax) ||
            hasAtLeastOneValue(incrementalProfitMin, incrementalProfitMax) ||
            hasAtLeastOneValue(retailerIncrementalProfitMin, retailerIncrementalProfitMax) ||
            hasAtLeastOneValue(retailerProfitMin, retailerProfitMax) ||
            hasAtLeastOneValue(incrementalNsvMin, incrementalNsvMax) ||
            (selectedCampaignTypes && selectedCampaignTypes.length > 0)
        );
    }, [
        promoTradeMin,
        promoTradeMax,
        tradeRateSpendMin,
        tradeRateSpendMax,
        totalVolumeMin,
        totalVolumeMax,
        totalNetSalesMin,
        totalNetSalesMax,
        totalRetailSalesMin,
        totalRetailSalesMax,
        totalGrossProfitMin,
        totalGrossProfitMax,
        incrementalVolumeMin,
        incrementalVolumeMax,
        incrementalProfitMin,
        incrementalProfitMax,
        retailerProfitMin,
        retailerProfitMax,
        incrementalNsvMin,
        incrementalNsvMax,
        selectedCampaignTypes,
        retailerIncrementalProfitMin,
        retailerIncrementalProfitMax,
    ]);

    const onOptimizePlan = async () => {
        setIsOptimizing(true);
        const constraints = [];

        // Helper function to create constraint object
        const createConstraint = (kpi: string, isMinMax: boolean, isMultiSelectKpi: boolean, details: any) => {
            return {
                objective: maximizeOption?.value || 'roi',
                kpi,
                isMinMax,
                isMultiSelectKpi,
                details,
            };
        };

        // Helper function to check if at least one value is present
        const hasAtLeastOneValue = (min: any, max: any) => (min && min !== '') || (max && max !== '');

        // Add ROI constraint if it has values
        if (hasAtLeastOneValue(promoTradeMin, promoTradeMax)) {
            constraints.push(
                createConstraint('ROI', true, false, {
                    max: promoTradeMax ? parseFloat(promoTradeMax) : null,
                    min: promoTradeMin ? parseFloat(promoTradeMin) : null,
                    value: null,
                    originalValue: parseFloat(optimizeOriginalData?.roi || '0'),
                    originalMin: null,
                    originalMax: null,
                }),
            );
        }

        // Add Total Promo Spends constraint if it has values
        if (hasAtLeastOneValue(tradeRateSpendMin, tradeRateSpendMax)) {
            constraints.push(
                createConstraint('Total Promo Spends', true, false, {
                    max: tradeRateSpendMax ? parseFloat(tradeRateSpendMax) : null,
                    min: tradeRateSpendMin ? parseFloat(tradeRateSpendMin) : null,
                    value: null,
                    originalValue: parseFloat(optimizeOriginalData?.totalTradeSpend || '0'),
                    originalMin: null,
                    originalMax: null,
                }),
            );
        }

        // Add Total Volume constraint if it has values
        if (hasAtLeastOneValue(totalVolumeMin, totalVolumeMax)) {
            constraints.push(
                createConstraint('Total Volume', true, false, {
                    max: totalVolumeMax ? parseFloat(totalVolumeMax) : null,
                    min: totalVolumeMin ? parseFloat(totalVolumeMin) : null,
                    value: null,
                    originalValue: parseFloat(optimizeOriginalData?.totalVolume || '0'),
                    originalMin: null,
                    originalMax: null,
                }),
            );
        }

        // Add Total Net Sales Value constraint if it has values
        if (hasAtLeastOneValue(totalNetSalesMin, totalNetSalesMax)) {
            constraints.push(
                createConstraint('Total Net Sales Value', true, false, {
                    max: totalNetSalesMax ? parseFloat(totalNetSalesMax) : null,
                    min: totalNetSalesMin ? parseFloat(totalNetSalesMin) : null,
                    value: null,
                    originalValue: parseFloat(optimizeOriginalData?.totalNetSales || '0'),
                    originalMin: null,
                    originalMax: null,
                }),
            );
        }

        // Add Total Retail Sales Value constraint if it has values
        if (hasAtLeastOneValue(totalRetailSalesMin, totalRetailSalesMax)) {
            constraints.push(
                createConstraint('Total Retail Sales Value', true, false, {
                    max: totalRetailSalesMax ? parseFloat(totalRetailSalesMax) : null,
                    min: totalRetailSalesMin ? parseFloat(totalRetailSalesMin) : null,
                    value: null,
                    originalValue: parseFloat(optimizeOriginalData?.totalRetailSales || '0'),
                    originalMin: null,
                    originalMax: null,
                }),
            );
        }

        // Add Total Gross Profit constraint if it has values
        if (hasAtLeastOneValue(totalGrossProfitMin, totalGrossProfitMax)) {
            constraints.push(
                createConstraint('Total Gross Profit', true, false, {
                    max: totalGrossProfitMax ? parseFloat(totalGrossProfitMax) : null,
                    min: totalGrossProfitMin ? parseFloat(totalGrossProfitMin) : null,
                    value: null,
                    originalValue: parseFloat(optimizeOriginalData?.totalGrossProfit || '0'),
                    originalMin: null,
                    originalMax: null,
                }),
            );
        }

        // Add Incremental Volume constraint if it has values
        if (hasAtLeastOneValue(incrementalVolumeMin, incrementalVolumeMax)) {
            constraints.push(
                createConstraint('Incremental Volume', true, false, {
                    max: incrementalVolumeMax ? parseFloat(incrementalVolumeMax) : null,
                    min: incrementalVolumeMin ? parseFloat(incrementalVolumeMin) : null,
                    value: null,
                    originalValue: parseFloat(optimizeOriginalData?.incrementalVolume || '0'),
                    originalMin: null,
                    originalMax: null,
                }),
            );
        }

        // Add Incremental Profit constraint if it has values
        if (hasAtLeastOneValue(incrementalProfitMin, incrementalProfitMax)) {
            constraints.push(
                createConstraint('Incremental Profit', true, false, {
                    max: incrementalProfitMax ? parseFloat(incrementalProfitMax) : null,
                    min: incrementalProfitMin ? parseFloat(incrementalProfitMin) : null,
                    value: null,
                    originalValue: parseFloat(optimizeOriginalData?.incrementalProfit || '0'),
                    originalMin: null,
                    originalMax: null,
                }),
            );
        }

        if (hasAtLeastOneValue(retailerIncrementalProfitMin, retailerIncrementalProfitMax)) {
            constraints.push(
                createConstraint('Incremental Retailer Profit', true, false, {
                    max: retailerIncrementalProfitMax ? parseFloat(retailerIncrementalProfitMax) : null,
                    min: retailerIncrementalProfitMin ? parseFloat(retailerIncrementalProfitMin) : null,
                    value: null,
                    originalValue: parseFloat(optimizeOriginalData?.retailerIncrementalProfit || '0'),
                    originalMin: null,
                    originalMax: null,
                }),
            );
        }

        // Add Total Retailer Profit constraint if it has values
        if (hasAtLeastOneValue(retailerProfitMin, retailerProfitMax)) {
            constraints.push(
                createConstraint('Total Retailer Profit', true, false, {
                    max: retailerProfitMax ? parseFloat(retailerProfitMax) : null,
                    min: retailerProfitMin ? parseFloat(retailerProfitMin) : null,
                    value: null,
                    originalValue: parseFloat(optimizeOriginalData?.totalRetailerProfit || '0'),
                    originalMin: null,
                    originalMax: null,
                }),
            );
        }

        // Add Incremental NSV constraint if it has values
        if (hasAtLeastOneValue(incrementalNsvMin, incrementalNsvMax)) {
            constraints.push(
                createConstraint('Incremental NSV', true, false, {
                    max: incrementalNsvMax ? parseFloat(incrementalNsvMax) : null,
                    min: incrementalNsvMin ? parseFloat(incrementalNsvMin) : null,
                    value: null,
                    originalValue: parseFloat(optimizeOriginalData?.incrementalNsv || '0'),
                    originalMin: null,
                    originalMax: null,
                }),
            );
        }

        // Add Keep Events constraint if it has values
        if (selectedCampaignTypes && selectedCampaignTypes.length > 0) {
            constraints.push(
                createConstraint('Keep Events', false, true, {
                    max: null,
                    min: null,
                    value: null,
                    originalValue: null,
                    originalMin: null,
                    originalMax: null,
                    multiSelectKpi: selectedCampaignTypes.map((campaign) => ({
                        value: campaign.value || campaign.eventId || campaign,
                        id: campaign.eventId || null,
                    })),
                }),
            );
        }

        const payload = {
            planId: selectedPlan?.planId,
            constraints,
        };

        const response: any = await optimizePlan({
            variables: payload,
        });
        if (response?.data) {
            onOptimizePlanCompleteWithAction;
        }
    };

    async function onOptimizePlanCompleteWithAction() {
        await fetchOptimizedPlan();
    }

    const allConstraintItems = [
        // {
        //     label: 'ROI',
        //     inputType: 'range' as const,
        //     originalValue: optimizeOriginalData?.percentageTradeRate,
        //     minValue: promoTradeMin,
        //     maxValue: promoTradeMax,
        //     onMinChange: setPromoTradeMin,
        //     onMaxChange: setPromoTradeMax,
        // },
        {
            label: 'Total Promo Spends',
            inputType: 'range' as const,
            originalValue: optimizeOriginalData?.totalTradeSpend,
            minValue: tradeRateSpendMin,
            maxValue: tradeRateSpendMax,
            onMinChange: setTradeRateSpendMin,
            onMaxChange: setTradeRateSpendMax,
        },
        // {
        //     label: 'Keep Events',
        //     inputType: 'dropdown' as const,
        //     dropdownValue: selectedCampaignTypes,
        //     onDropdownChange: setSelectedCampaignTypes,
        //     dropdownOptions: eventOptions,
        //     dropdownPlaceholder: 'Select Keep Events',
        //     isMultiSelect: true,
        // },
        {
            label: 'Total Volume',
            inputType: 'range' as const,
            originalValue: optimizeOriginalData?.totalVolume,
            minValue: totalVolumeMin,
            maxValue: totalVolumeMax,
            onMinChange: setTotalVolumeMin,
            onMaxChange: setTotalVolumeMax,
        },
        {
            label: 'Total Net Sales Value',
            inputType: 'range' as const,
            originalValue: optimizeOriginalData?.totalNetSales,
            minValue: totalNetSalesMin,
            maxValue: totalNetSalesMax,
            onMinChange: setTotalNetSalesMin,
            onMaxChange: setTotalNetSalesMax,
        },
        // {
        //     label: 'Total Retail Sales',
        //     inputType: 'range' as const,
        //     originalValue: optimizeOriginalData?.totalRetailSales,
        //     minValue: totalRetailSalesMin,
        //     maxValue: totalRetailSalesMax,
        //     onMinChange: setTotalRetailSalesMin,
        //     onMaxChange: setTotalRetailSalesMax,
        // },
        {
            label: 'Total Gross Profit',
            inputType: 'range' as const,
            originalValue: optimizeOriginalData?.totalGrossProfit,
            minValue: totalGrossProfitMin,
            maxValue: totalGrossProfitMax,
            onMinChange: setTotalGrossProfitMin,
            onMaxChange: setTotalGrossProfitMax,
        },
        {
            label: 'Incremental Volume',
            inputType: 'range' as const,
            originalValue: optimizeOriginalData?.incrementalVolume,
            minValue: incrementalVolumeMin,
            maxValue: incrementalVolumeMax,
            onMinChange: setIncrementalVolumeMin,
            onMaxChange: setIncrementalVolumeMax,
        },
        {
            label: 'Incremental Profit',
            inputType: 'range' as const,
            originalValue: optimizeOriginalData?.incrementalProfit,
            minValue: incrementalProfitMin,
            maxValue: incrementalProfitMax,
            onMinChange: setIncrementalProfitMin,
            onMaxChange: setIncrementalProfitMax,
        },
        {
            label: 'Incremental Retailer Profit',
            inputType: 'range' as const,
            originalValue: optimizeOriginalData?.retailerIncrementalProfit,
            minValue: retailerIncrementalProfitMin,
            maxValue: retailerIncrementalProfitMax,
            onMinChange: setRetailerIncrementalProfitMin,
            onMaxChange: setRetailerIncrementalProfitMax,
        },
        {
            label: 'Total Retailer Profit',
            inputType: 'range' as const,
            originalValue: optimizeOriginalData?.totalRetailerProfit,
            minValue: retailerProfitMin,
            maxValue: retailerProfitMax,
            onMinChange: setRetailerProfitMin,
            onMaxChange: setRetailerProfitMax,
        },
        {
            label: 'Incremental NSV',
            inputType: 'range' as const,
            originalValue: optimizeOriginalData?.incrementalNsv,
            minValue: incrementalNsvMin,
            maxValue: incrementalNsvMax,
            onMinChange: setIncrementalNsvMin,
            onMaxChange: setIncrementalNsvMax,
        },
    ];

    //modal footer
    const Footer = () => {
        return (
            <div className="w-full flex justify-end gap-2">
                <Button title={'Cancel'} onClick={handleCancel} style={{ padding: '0px 20px' }} disabled={false} />

                <Button
                    title={
                        optimizePlanLoading ? (
                            <div className="flex justify-center items-center">
                                <Spin indicator={antIcon} />
                            </div>
                        ) : (
                            'Run Optimization'
                        )
                    }
                    onClick={onOptimizePlan}
                    theme={'Solid'}
                    style={{ padding: '0px 20px' }}
                    disabled={optimizeOriginalLoading || !maximizeOption?.value || !isAnyConstraintSelected}
                />
            </div>
        );
    };

    return (
        <CustomModal
            title="Optimize"
            open={openOptimizeDialogBox}
            width={620}
            onCancel={handleCancel}
            centered
            footer={Footer()}
            maskClosable={false}
        >
            <div className="px-4">
                <div className="flex justify-between items-center">
                    <div className="flex flex-col mb-2 ">
                        <span className="text-dark font-xs">
                            <span className="mr-2 font-bold">Objective</span>
                        </span>
                        <Cascader
                            style={{ width: '170px' }}
                            suffixIcon={<DownSvg />}
                            value={maximizeOption?.fullPath}
                            placeholder="Select Objective"
                            options={cascaderOptions}
                            className="dropdown-height dropdown-width-alt"
                            onChange={(value, selectedOptions) => {
                                if (selectedOptions && selectedOptions.length > 0) {
                                    const lastOption = selectedOptions[selectedOptions.length - 1];
                                    setMaximizeOption({
                                        value: lastOption.value,
                                        label: lastOption.label,
                                        fullPath: value,
                                    });
                                } else {
                                    setMaximizeOption(null);
                                }
                            }}
                            displayRender={(labels) => {
                                if (maximizeOption?.label) {
                                    return maximizeOption.label;
                                }
                                return 'Select Objective';
                            }}
                        />
                    </div>
                </div>

                <div>
                    <span className="mr-2 mb-1 font-bold font-xs ">Constraints</span>

                    {optimizeOriginalLoading ? (
                        <div
                            className="border-collapse border border-borderGrey  mb-4 rounded-t text-purpleFont overflow-auto flex justify-center items-center"
                            style={{ height: '262px' }}
                        >
                            <Spin />
                        </div>
                    ) : (
                        // KPI TABLE
                        <div
                            style={{ height: '262px' }}
                            className="border-collapse border border-borderGrey mt-2 mb-4 rounded-t text-purpleFont overflow-auto"
                        >
                            <div className="flex w-full justify-between bg-bgBackground border-b-1 border-borderGrey rounded font-medium sticky top-0 z-[100]">
                                <div className="flex w-full cursor-pointer">
                                    <div className="flex py-2 w-full">
                                        <div className="w-1/3 ml-4">
                                            <span>KPI</span>
                                        </div>
                                        <div className="w-1/3 text-center mr-12">Original Value</div>
                                        <div className="flex justify-center w-1/3 text-center mr-10">Input</div>
                                    </div>
                                </div>
                            </div>

                            {allConstraintItems.map((item, index) => (
                                <ConstraintRow
                                    key={index}
                                    originalValue={item.originalValue}
                                    label={item.label}
                                    inputType={item.inputType}
                                    minValue={item.minValue}
                                    maxValue={item.maxValue}
                                    onMinChange={item.onMinChange}
                                    onMaxChange={item.onMaxChange}
                                    // dropdownValue={item.dropdownValue}
                                    // onDropdownChange={item.onDropdownChange}
                                    // dropdownOptions={item.dropdownOptions}
                                    // isMultiSelect={item.isMultiSelect}
                                />
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </CustomModal>
    );
};

export default OptimizeScenarioModel;
